/* MOJO PDP V2 below-the-fold interactions (deferred, delegated, no dependencies).
   UGC videos · "Elas usam" row arrows · Before/After comparison + example selection · YouTube facade. */
(() => {
  if (window.__mojoChapters) return;
  window.__mojoChapters = true;
  const reduce = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const pub = (n, d) => { try { window.Shopify && Shopify.analytics && Shopify.analytics.publish(n, d || {}); } catch (e) { /* noop */ } };

  /* Before/After slider: range input -> CSS var, one update per frame */
  let raf = 0; let pending = null;
  document.addEventListener('input', (e) => {
    const r = e.target;
    if (!r.classList || !r.classList.contains('mojo-ba2__range')) return;
    pending = r;
    if (raf) return;
    raf = requestAnimationFrame(() => {
      raf = 0;
      const box = pending.closest('[data-mojo-ba]');
      const v = pending.value;
      box.classList.remove('is-hinting');
      box.style.setProperty('--pos', `${v}%`);
      pending.setAttribute('aria-valuetext', `${v}% antes, ${100 - v}% depois`);
      if (!box.dataset.used) { box.dataset.used = '1'; pub('mojo_before_after_interaction', { action: 'drag' }); }
    });
  });

  /* Before/After: one control per example (tab card, peek card or anything inside them) */
  const select = (box, id) => {
    const cards = [...box.querySelectorAll('.mojo-bag__card')];
    const idx = cards.findIndex((c) => c.id === id);
    if (idx < 0) return;
    cards.forEach((c, j) => {
      const pos = (j - idx + cards.length) % cards.length;
      c.dataset.pos = pos > 2 ? 'hidden' : String(pos);
      const active = pos === 0;
      c.classList.toggle('is-active', active);
      c.setAttribute('aria-hidden', String(!active));
      const range = c.querySelector('.mojo-ba2__range');
      if (range) range.tabIndex = active ? 0 : -1;
      if (active) {
        const tpl = c.querySelector('template[data-mojo-ba-tpl]');
        if (tpl) { c.prepend(tpl.content.cloneNode(true)); tpl.remove(); const pv = c.querySelector('.mojo-bag__preview'); if (pv) pv.remove(); }
      }
    });
    box.querySelectorAll('[data-mojo-ba-tab]').forEach((t) => {
      const on = t.getAttribute('aria-controls') === id;
      t.setAttribute('aria-selected', String(on));
      t.tabIndex = on ? 0 : -1;
      if (on) t.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: reduce() ? 'auto' : 'smooth' });
    });
    pub('mojo_before_after_interaction', { action: 'select', item: id });
  };
  document.addEventListener('click', (e) => {
    const box = e.target.closest('[data-mojo-bagallery]');
    if (!box) return;
    const tab = e.target.closest('[data-mojo-ba-tab]');
    if (tab) { select(box, tab.getAttribute('aria-controls')); return; }
    const peek = e.target.closest('[data-mojo-ba-peek]');
    if (peek) { select(box, peek.getAttribute('data-mojo-ba-peek')); return; }
    const nav = e.target.closest('[data-mojo-ba-prev], [data-mojo-ba-next]');
    if (nav) {
      const tabs = [...box.querySelectorAll('[data-mojo-ba-tab]')];
      const i = tabs.findIndex((t) => t.getAttribute('aria-selected') === 'true');
      const n = tabs[(i + (nav.hasAttribute('data-mojo-ba-next') ? 1 : tabs.length - 1)) % tabs.length];
      if (n) select(box, n.getAttribute('aria-controls'));
    }
  });
  document.addEventListener('keydown', (e) => {
    const t = e.target.closest && e.target.closest('[data-mojo-ba-tab]');
    if (!t || (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft')) return;
    const tabs = [...t.parentElement.querySelectorAll('[data-mojo-ba-tab]')];
    const i = tabs.indexOf(t);
    const n = tabs[(i + (e.key === 'ArrowRight' ? 1 : tabs.length - 1)) % tabs.length];
    n.focus(); n.click(); e.preventDefault();
  });

  /* ---------- Motion (progressive enhancement; everything is already in its final state without it) ----------
     Once-per-visit memory so sibling pages (colour/length) don't replay the same choreography. */
  const once = {
    has(k) { try { return (sessionStorage.getItem('mojo-motion') || '').split('|').indexOf(k) > -1; } catch (e) { return true; } },
    set(k) { try { sessionStorage.setItem('mojo-motion', (sessionStorage.getItem('mojo-motion') || '') + '|' + k); } catch (e) { /* noop */ } }
  };
  const canMove = () => !reduce() && 'IntersectionObserver' in window;
  const desktop = () => window.matchMedia('(min-width: 990px)').matches;

  /* Editorial heading devices: loop draws / spike opens once, when the heading first comes into view.
     Headings already on screen at load, or already seen this visit, are simply shown. No layout change. */
  let headIO = null;
  const scanHeads = () => {
    if (!canMove()) return;
    headIO = headIO || new IntersectionObserver((es) => es.forEach((en) => {
      if (!en.isIntersecting) return;
      headIO.unobserve(en.target);
      once.set(en.target.dataset.mojoHead);
      requestAnimationFrame(() => { en.target.classList.add('is-draw'); en.target.classList.remove('is-pre'); });
    }), { threshold: 1, rootMargin: '0px 0px -12% 0px' });
    document.querySelectorAll('.mojo-ch__head > .mojo-deco:not([data-mojo-head])').forEach((d) => {
      const h = d.parentElement.querySelector('h2');
      const key = 'h:' + (h ? h.textContent.trim() : '');
      d.dataset.mojoHead = key;
      if (once.has(key) || d.getBoundingClientRect().top < window.innerHeight) return;
      d.classList.add('is-pre');
      headIO.observe(d);
    });
  };

  /* Before/After: section rises once; the divider hints "drag me" once per visit, only when well in view */
  const arm = () => {
    const boxes = document.querySelectorAll('[data-mojo-bagallery]:not(.is-armed)');
    if (!boxes.length) return;
    if (!('IntersectionObserver' in window)) { boxes.forEach((b) => b.classList.add('is-armed', 'is-revealed')); return; }
    const io = new IntersectionObserver((es) => es.forEach((en) => {
      if (!en.isIntersecting) return;
      io.unobserve(en.target);
      en.target.classList.add('is-revealed');
    }), { threshold: 0.3 });
    boxes.forEach((b) => { b.classList.add('is-armed'); io.observe(b); });
    if (!canMove() || once.has('ba-hint')) return;
    const hio = new IntersectionObserver((es) => es.forEach((en) => {
      if (!en.isIntersecting) return;
      hio.disconnect();
      const ba = en.target;
      if (ba.dataset.used || once.has('ba-hint')) return;
      once.set('ba-hint');
      setTimeout(() => { if (ba.dataset.used) return; ba.classList.add('is-hinting'); ba.style.setProperty('--pos', '38%'); }, 500);
      setTimeout(() => { if (!ba.dataset.used) ba.style.setProperty('--pos', '50%'); }, 1250);
      setTimeout(() => ba.classList.remove('is-hinting'), 2000);
    }), { threshold: 0.65 });
    boxes.forEach((b) => { const ba = b.querySelector('.mojo-bag__card.is-active [data-mojo-ba]'); if (ba) hio.observe(ba); });
  };

  /* Elas usam MOJO (mobile): one gentle sideways nudge that says "swipe" — once per visit, never autoplay */
  const nudge = () => {
    const tr = document.querySelector('.mojo-ch--ugcv [data-mojo-ugc-track]');
    if (!tr || !canMove() || desktop() || !tr.animate || once.has('ugc-nudge')) return;
    const io = new IntersectionObserver((es) => es.forEach((en) => {
      if (!en.isIntersecting) return;
      io.disconnect();
      if (tr.scrollLeft > 4 || tr.scrollWidth <= tr.clientWidth + 8) return;
      once.set('ugc-nudge');
      setTimeout(() => tr.animate(
        [{ transform: 'translateX(0)' }, { transform: 'translateX(-36px)', offset: 0.42 }, { transform: 'translateX(0)' }],
        { duration: 1000, easing: 'cubic-bezier(.35, 0, .2, 1)' }
      ), 300);
    }), { threshold: 0.6 });
    io.observe(tr);
  };

  const init = () => { arm(); scanHeads(); nudge(); };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init); else init();
  document.addEventListener('mojo:content', scanHeads);

  /* Horizontal rows (UGC, Before/After selector): arrows scroll by one item */
  document.addEventListener('click', (e) => {
    const b = e.target.closest('[data-mojo-ugc-prev], [data-mojo-ugc-next]');
    if (!b) return;
    const tr = b.closest('.mojo-ch').querySelector('[data-mojo-ugc-track]');
    const item = tr.querySelector(':scope > *');
    const step = item ? item.getBoundingClientRect().width + 16 : tr.clientWidth * 0.8;
    tr.scrollBy({ left: b.hasAttribute('data-mojo-ugc-next') ? step : -step, behavior: reduce() ? 'auto' : 'smooth' });
  });

  /* UGC video: load on tap, one at a time, pause off-screen, original audio */
  const io = 'IntersectionObserver' in window ? new IntersectionObserver((es) => es.forEach((en) => { if (!en.isIntersecting) en.target.pause(); }), { threshold: 0.25 }) : null;
  document.addEventListener('click', (e) => {
    const b = e.target.closest('[data-mojo-ugc-play]');
    if (!b) return;
    const box = b.closest('[data-mojo-ugc]');
    document.querySelectorAll('[data-mojo-ugc] video').forEach((v) => v.pause());
    const apple = /Apple/.test(navigator.vendor || '');
    const src = (apple && box.dataset.hls) || box.dataset.mp4 || box.dataset.hls;
    if (!src) return;
    const v = document.createElement('video');
    v.src = src; v.controls = true; v.playsInline = true; v.setAttribute('playsinline', ''); v.preload = 'auto';
    v.className = 'mojo-ugcv__video';
    v.setAttribute('aria-label', b.getAttribute('aria-label').replace('Reproduzir vídeo', 'Vídeo'));
    const poster = box.querySelector('img'); if (poster) v.poster = poster.currentSrc || poster.src;
    box.replaceChildren(v);
    v.addEventListener('play', () => document.querySelectorAll('[data-mojo-ugc] video').forEach((o) => { if (o !== v) o.pause(); }));
    if (io) io.observe(v);
    v.play().catch(() => {});
    pub('mojo_ugc_video_play', { index: [...document.querySelectorAll('[data-mojo-ugc]')].indexOf(box) + 1 });
  });

  /* Rating evidence gate.
     reviews.rating / reviews.rating_count are written by the reviews app and are shared across its product group, so
     a sibling can carry "5,0 - 11 avaliacoes" while rendering zero readable records on its own page. Anything the
     page cannot show, the page must not claim. Elements marked [data-mojo-rating-gated] ship hidden and are revealed
     only once a real review record exists in the widget slot. If none arrives, the whole rating head collapses and
     the section keeps an accessible name of its own. */
  (() => {
    const gated = () => [...document.querySelectorAll('[data-mojo-rating-gated]')];
    if (!gated().length) return;
    const slot = () => document.querySelector('[data-mojo-reviews-slot]');
    const RECORD = '[data-review-id], [class*="review-card"]';
    const countRecords = () => { const s = slot(); return s ? s.querySelectorAll(RECORD).length : 0; };
    let settled = false;
    const reveal = () => {
      if (settled) return; settled = true;
      gated().forEach((el) => { el.hidden = false; el.removeAttribute('data-mojo-rating-gated'); });
      pub('mojo_rating_gate', { result: 'shown' });
    };
    const collapse = () => {
      if (settled) return; settled = true;
      gated().forEach((el) => el.remove());
      const sec = document.querySelector('[data-mojo-rev-gate]');
      if (sec) {
        const head = sec.querySelector('.mojo-rev-head');
        if (head) head.hidden = true;
        /* the visible <h2> is gone, so give the landmark a name that does not depend on it */
        sec.removeAttribute('aria-labelledby');
        sec.setAttribute('aria-label', 'Avaliações');
        sec.classList.add('mojo-ch--reviews-empty');
      }
      pub('mojo_rating_gate', { result: 'hidden_no_records' });
    };
    const check = () => { if (countRecords() > 0) { reveal(); return true; } return false; };
    if (check()) return;
    const s = slot();
    let obs = null;
    if (s && window.MutationObserver) {
      obs = new MutationObserver(() => { if (check() && obs) obs.disconnect(); });
      obs.observe(s, { childList: true, subtree: true });
    }
    /* Cap the undecided window: the reviews app renders client-side and may never render anything at all. */
    setTimeout(() => { if (obs) obs.disconnect(); if (!check()) collapse(); }, 8000);
  })();

  /* Judge.me group reviews rail (reviews block, source = judgeme).
     Page 1 is parsed from the server-rendered judgeme.widget metafield, so the rail works with no network call.
     With a PUBLIC Judge.me token configured, further pages come from Judge.me's public widget endpoint (published
     reviews only) and visitors can submit a review, which lands in Judge.me moderation. Read-only otherwise. */
  (() => {
    const root = document.querySelector('[data-mojo-jm]');
    if (!root) return;
    const track = root.querySelector('[data-mojo-jm-track]');
    const prev = root.querySelector('[data-mojo-jm-prev]');
    const next = root.querySelector('[data-mojo-jm-next]');
    const token = root.dataset.token || '';
    const API = 'https://judge.me/api/v1';
    const PER = 10;
    const all = [];
    const seen = new Set();
    let page = 0, busy = false, done = !token;
    let zoom = null, sheet = null, write = null;
    const esc = (s) => String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
    const shortName = (n) => { const p = String(n).trim().split(/\s+/).filter(Boolean); return p.length > 1 ? p[0] + ' ' + p[p.length - 1][0].toUpperCase() + '.' : p[0] || 'Cliente'; };
    const when = (iso) => { const d = new Date(iso); return isNaN(d) ? '' : d.toLocaleDateString('pt-BR', { month: 'short', year: 'numeric' }).replace('.', ''); };
    const parse = (html) => {
      const doc = new DOMParser().parseFromString(html || '', 'text/html');
      return [...doc.querySelectorAll('.jdgm-rev[data-review-id]')].map((el) => ({
        id: el.dataset.reviewId,
        verified: el.dataset.verifiedBuyer === 'true',
        score: Math.max(1, Math.min(5, +(el.querySelector('.jdgm-rev__rating')?.dataset.score || 5))),
        author: el.querySelector('.jdgm-rev__author')?.textContent || '',
        date: el.querySelector('.jdgm-rev__timestamp')?.getAttribute('datetime') || '',
        body: (el.querySelector('.jdgm-rev__body')?.textContent || '').trim(),
        pics: [...el.querySelectorAll('.jdgm-rev__pic-link')].map((a) => a.getAttribute('href')).filter((u) => /^https:\/\//.test(u || ''))
      }));
    };
    const card = (r, tag) => {
      const pic = r.pics[0];
      const who = esc(shortName(r.author));
      return `<${tag} class="mojo-jm__card${pic ? '' : ' mojo-jm__card--text'}" data-review-id="${esc(r.id)}">
        ${pic ? `<button type="button" class="mojo-jm__photo" data-mojo-jm-zoom="${esc(pic)}" aria-label="Ampliar foto de ${who}"><img src="${esc(pic.replace(/width=\d+/, 'width=480'))}" alt="Foto enviada por ${who}" loading="lazy" decoding="async" width="480" height="600">${r.pics.length > 1 ? `<span class="mojo-jm__more">+${r.pics.length - 1}</span>` : ''}</button>` : ''}
        <div class="mojo-jm__txt">
          <span class="mojo-stars" style="--mojo-stars:${r.score * 20}%" aria-hidden="true"></span><span class="visually-hidden">${r.score} de 5 estrelas</span>
          ${r.body ? `<p class="mojo-jm__body">${esc(r.body)}</p>` : ''}
          <p class="mojo-jm__by"><strong>${who}</strong>${r.verified ? ' <span class="mojo-jm__ver">Compra verificada</span>' : ''}<span class="mojo-jm__date">${when(r.date)}</span></p>
        </div></${tag}>`;
    };
    /* Presentation order (never changes Judge.me records): reviews with photos first, then text-only;
       newest first inside each group. Applied to everything loaded so far, so later pages slot in correctly. */
    const order = (a, b) => ((b.pics.length > 0) - (a.pics.length > 0)) || ((Date.parse(b.date) || 0) - (Date.parse(a.date) || 0));
    const render = () => {
      const sorted = [...all].sort(order);
      track.innerHTML = sorted.map((r) => card(r, 'li')).join('');
      const list2 = sheet && sheet.querySelector('[data-mojo-jm-list]');
      if (list2) list2.innerHTML = sorted.map((r) => card(r, 'div')).join('');
      nav();
    };
    const add = (list) => {
      const fresh = list.filter((r) => r.id && !seen.has(r.id) && (r.body || r.pics.length));
      fresh.forEach((r) => seen.add(r.id));
      if (fresh.length) { all.push(...fresh); render(); }
      return fresh.length;
    };
    const more = async () => {
      if (busy || done) return 0;
      busy = true; page += 1;
      let n = 0;
      try {
        const u = `${API}/widgets/product_review?api_token=${encodeURIComponent(token)}&shop_domain=${encodeURIComponent(root.dataset.shop)}&handle=${encodeURIComponent(root.dataset.handle)}&page=${page}&per_page=${PER}`;
        const res = await fetch(u, { credentials: 'omit' });
        const list = res.ok ? parse((await res.json()).widget) : [];
        if (!res.ok || list.length < PER) done = true;
        n = add(list);
      } catch (_) { done = true; }
      busy = false;
      if (sheet) sheet.querySelector('[data-mojo-jm-load]').hidden = done;
      return n;
    };
    const nav = () => {
      const max = track.scrollWidth - track.clientWidth;
      prev.hidden = track.scrollLeft < 8;
      next.hidden = max < 8 || (track.scrollLeft > max - 8 && done);
      if (!done && track.scrollLeft > max - track.clientWidth * 1.5) more();
    };
    const seed = root.querySelector('template[data-mojo-jm-seed]');
    if (seed) { add(parse(seed.innerHTML)); seed.remove(); }
    if (!all.length && !done) more();
    /* With a public token, load every page (capped) once the section is near, so photo reviews from later pages
       can lead the rail. Groups here are small (tens of reviews). */
    if (!done && 'IntersectionObserver' in window) {
      const io = new IntersectionObserver(async (en) => {
        if (!en.some((x) => x.isIntersecting)) return;
        io.disconnect();
        for (let i = 0; i < 10 && !done; i++) { while (busy) await new Promise((r) => setTimeout(r, 50)); await more(); }
      }, { rootMargin: '600px 0px' });
      io.observe(root);
    }
    track.addEventListener('scroll', nav, { passive: true });
    window.addEventListener('resize', nav, { passive: true });
    const step = (d) => { track.scrollBy({ left: d * track.clientWidth * 0.85, behavior: 'smooth' }); pub('mojo_reviews_rail', { action: d > 0 ? 'next' : 'prev' }); };
    prev.addEventListener('click', () => step(-1));
    next.addEventListener('click', () => step(1));

    const dialog = (cls, html) => {
      const d = document.createElement('dialog');
      d.className = 'mojo-jm__dlg ' + cls;
      d.innerHTML = html;
      d.addEventListener('click', (e) => { if (e.target === d || e.target.closest('[data-mojo-jm-close]')) d.close(); });
      document.body.append(d);
      return d;
    };
    document.addEventListener('click', (e) => {
      const b = e.target.closest('[data-mojo-jm-zoom]');
      if (!b) return;
      zoom = zoom || dialog('mojo-jm__zoom', '');
      zoom.innerHTML = `<img src="${esc(b.dataset.mojoJmZoom)}" alt="${esc(b.getAttribute('aria-label'))}"><button type="button" class="mojo-jm__close" data-mojo-jm-close aria-label="Fechar">×</button>`;
      zoom.showModal();
      pub('mojo_reviews_rail', { action: 'photo_open' });
    });
    const allBtn = root.querySelector('[data-mojo-jm-all]');
    if (allBtn) allBtn.addEventListener('click', async () => {
      if (!sheet) {
        sheet = dialog('mojo-jm__sheet', `<div class="mojo-jm__sheet-head"><h3>Avaliações de clientes</h3><button type="button" class="mojo-jm__close" data-mojo-jm-close aria-label="Fechar">×</button></div>
          <div class="mojo-jm__list" data-mojo-jm-list>${[...all].sort(order).map((r) => card(r, 'div')).join('')}</div>
          <button type="button" class="mojo-jm__btn mojo-jm__btn--ghost" data-mojo-jm-load${done ? ' hidden' : ''}>Carregar mais</button>`);
        sheet.querySelector('[data-mojo-jm-load]').addEventListener('click', more);
      }
      sheet.showModal();
      pub('mojo_reviews_rail', { action: 'view_all' });
      if (!done && sheet.querySelectorAll('[data-review-id]').length < PER * 2) more();
    });
    const wBtn = root.querySelector('[data-mojo-jm-write]');
    if (wBtn) wBtn.addEventListener('click', () => {
      if (!write) {
        write = dialog('mojo-jm__sheet mojo-jm__write', `<div class="mojo-jm__sheet-head"><h3>Escrever uma avaliação</h3><button type="button" class="mojo-jm__close" data-mojo-jm-close aria-label="Fechar">×</button></div>
          <form class="mojo-jm__form" novalidate>
            <fieldset class="mojo-jm__rate"><legend>Sua nota</legend>${[5, 4, 3, 2, 1].map((n) => `<input type="radio" name="rating" id="mojo-jm-r${n}" value="${n}" required><label for="mojo-jm-r${n}" aria-label="${n} estrelas">★</label>`).join('')}</fieldset>
            <label>Seu nome<input name="name" autocomplete="given-name" required maxlength="80"></label>
            <label>Seu e-mail <small>(não será publicado)</small><input name="email" type="email" autocomplete="email" required maxlength="120"></label>
            <label>Sua avaliação<textarea name="body" rows="4" required maxlength="2000"></textarea></label>
            <p class="mojo-jm__msg" role="status" aria-live="polite"></p>
            <button type="submit" class="mojo-jm__btn">Enviar avaliação</button>
          </form>`);
        const f = write.querySelector('form');
        f.addEventListener('submit', async (ev) => {
          ev.preventDefault();
          const msg = f.querySelector('.mojo-jm__msg');
          const fd = new FormData(f);
          if (!fd.get('rating') || !f.checkValidity()) { msg.textContent = 'Preencha nota, nome, e-mail e avaliação.'; return; }
          const btn = f.querySelector('[type=submit]'); btn.disabled = true; msg.textContent = 'Enviando…';
          try {
            const res = await fetch(`${API}/reviews`, { method: 'POST', credentials: 'omit', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({
              api_token: token, shop_domain: root.dataset.shop, platform: 'shopify', id: +root.dataset.productId,
              name: fd.get('name'), email: fd.get('email'), rating: +fd.get('rating'), body: fd.get('body'), title: '' }) });
            if (!res.ok) throw new Error(res.status);
            f.innerHTML = '<p class="mojo-jm__thanks">Obrigada! Sua avaliação foi recebida e aparece aqui depois da moderação.</p>';
            pub('mojo_reviews_rail', { action: 'review_submitted' });
          } catch (_) {
            btn.disabled = false; msg.textContent = 'Não foi possível enviar agora. Tente novamente em instantes.';
          }
        });
      }
      write.showModal();
      pub('mojo_reviews_rail', { action: 'write_open' });
    });
  })();

  /* Theme-asset video facade (family how-to): the mp4 is only requested on tap. */
  document.addEventListener('click', (e) => {
    const b = e.target.closest('[data-mojo-vid-play]');
    if (!b) return;
    const w = b.closest('[data-mojo-vid]');
    const v = document.createElement('video');
    v.src = w.dataset.src; v.controls = true; v.playsInline = true; v.autoplay = true; v.preload = 'auto';
    v.setAttribute('aria-label', b.getAttribute('aria-label'));
    const poster = w.querySelector('img'); if (poster) v.poster = poster.currentSrc || poster.src;
    w.replaceChildren(v); w.classList.add('is-playing');
    v.play().catch(() => {});
    pub('mojo_gallery_interaction', { action: 'how_video_play' });
  });

  /* YouTube facade (Como funciona) */
  document.addEventListener('click', (e) => {
    const b = e.target.closest('[data-mojo-yt-play]');
    if (!b) return;
    const w = b.closest('[data-mojo-yt]');
    const f = document.createElement('iframe');
    f.src = w.dataset.src; f.title = b.getAttribute('aria-label');
    f.allow = 'autoplay; encrypted-media; picture-in-picture'; f.allowFullscreen = true;
    w.replaceChildren(f); w.classList.add('is-playing');
    pub('mojo_gallery_interaction', { action: 'how_video_play' });
  });
})();
